import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  SetStateAction
} from 'react'
import styled from '@emotion/styled'
import {
  TrackableEvent,
  trackUserActionsFBPixel,
  trackUserActionsGA4
} from 'tracker'
import QuantityControl from '../QuantityControl/QuantityControl'
import { StyledHTMLElement } from 'shop/theme/types'
interface VariantControlType {
  productId: string
  productVariantName: string
  productVariantLimit: number | null
  inStock: boolean
  title?: string
  nextSection?: string
  variantQuantity: number
  setVariantQuantity: React.Dispatch<SetStateAction<number>>
}

const VariantControl = ({
  productId,
  productVariantName,
  productVariantLimit,
  inStock,
  title,
  nextSection,
  variantQuantity,
  setVariantQuantity
}: VariantControlType) => {
  const atMaxQuantity = useMemo(() => {
    if (variantQuantity >= 50) return true // Be reasonable, who orders more than 50 doughnuts at once?
    if (productVariantLimit === null) return false
    if (productVariantLimit > 0 && variantQuantity >= productVariantLimit)
      return true
    return false
  }, [productVariantLimit, variantQuantity])

  // Track Product Quantity changes
  const track = useCallback(
    (newQuantity: number) => {
      const body = {
        category: productVariantName,
        action: TrackableEvent.ProductCustomized,
        label: productVariantName,
        value: newQuantity
      }

      trackUserActionsGA4(body, 'slerpGA4Tracking')

      // legacy tracking
      trackUserActionsFBPixel(TrackableEvent.ProductCustomized, {
        content_name: productVariantName,
        content_type: 'product',
        contents: [{ id: productId, quantity: newQuantity }]
      })
      trackUserActionsGA4(body, 'merchantGA4Tracking')
    },
    [productId, productVariantName]
  )

  //  TODO: Check order limit
  const incrementQuantity = () => {
    if (atMaxQuantity) return

    const newQuantity = variantQuantity + 1
    setVariantQuantity(newQuantity)
    track(newQuantity)
  }

  const decrementQuantity = () => {
    if (variantQuantity === 0) return
    const newQuantity = variantQuantity - 1
    if (newQuantity <= 0) {
      setVariantQuantity(0)
    } else {
      setVariantQuantity(newQuantity)
    }
    track(newQuantity)
  }

  const [active, setActive] = useState('')
  useEffect(() => {
    if (variantQuantity > 0) {
      setActive('active')
    } else {
      setActive('')
    }
  }, [variantQuantity])

  return (
    <Container className={active}>
      <div>
        {title && <Name>{title}</Name>}
        <QuantityControl
          quantity={variantQuantity}
          onIncrement={incrementQuantity}
          onDecrement={decrementQuantity}
          disableDecrement={variantQuantity === 1 || !inStock || !!nextSection}
          disableIncrement={!inStock || atMaxQuantity || !!nextSection}
          quantityOpacity={inStock ? 1 : 0.2}
        />
      </div>
    </Container>
  )
}

const Container = styled.a<StyledHTMLElement>(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}))

const Name = styled.h4(({ theme }) => ({}))

export default VariantControl
